import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { roleApi } from "./api";
import { permissionApi } from "../permissions/api";
import type {
  PermissionOption,
  RoleCreateRequest,
  RoleResponse,
  RoleUpdateRequest,
} from "./types";

export function useRoles() {
  return useQuery<RoleResponse[], Error>({
    queryKey: ["roles"],
    queryFn: () => roleApi.list(),
  });
}

export function useRole(roleId?: number) {
  return useQuery<RoleResponse, Error>({
    queryKey: ["role", roleId],
    queryFn: () => roleApi.getById(roleId as number),
    enabled: typeof roleId === "number" && !Number.isNaN(roleId) && roleId > 0,
  });
}

export function usePermissionOptions(search?: string) {
  return useQuery<PermissionOption[], Error>({
    queryKey: ["permission-options", search ?? ""],
    queryFn: () => permissionApi.list(search),
    staleTime: 1000 * 60 * 10,
  });
}

export function useCreateRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: RoleCreateRequest) => roleApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
    },
  });
}

export function useUpdateRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number;
      payload: RoleUpdateRequest;
    }) => roleApi.update(id, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      queryClient.invalidateQueries({ queryKey: ["role", variables.id] });
    },
  });
}

export function useDeleteRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => roleApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
    },
  });
}